import Alert from "@mui/material/Alert";
import Skeleton from "@mui/material/Skeleton";
import { useTheme } from "@mui/material/styles";
import * as React from "react";
import { Link } from "react-router";

import CopyToClipboardButton from "@/shared/components/CopyToClipboardButton";
import {
  ResourceView,
  composeFilters,
  useResourceView,
  type Filter,
  type FilterDefinition,
  type FilterMatchMode,
  type FilterOptionsResolver,
} from "@/shared/components/ResourceView";
import { useAdminAgentsByIds } from "@/shared/domain/admin/hooks/useAdminAgentsByIds";
import { useAdminAgentThreadSearch } from "@/shared/domain/admin/hooks/useAdminAgentThreadSearch";
import { useAdminFeedbackCountsForAgentThreads } from "@/shared/domain/admin/hooks/useAdminFeedback";
import {
  AgentThreadStatus,
  ThreadVisibility,
  type AgentThreadRecord,
} from "@/shared/domain/agentThread";
import {
  ThumbDownAltIcon,
  ThumbUpAltIcon,
} from "@/shared/foundation/icons/mui";
import {
  ColumnVisibility,
  DataTable,
  cellStyles,
  colId,
  createColumnHelper,
  type ColDef,
  type ColumnPickerOption,
  deriveLoadingState,
  type ICellRendererParams,
  type ValueFormatterParams,
} from "@/shared/foundation/primitives/Table";
import { Goto } from "@/shared/navigation";
import type { SearchQueryBody, SortDirection } from "@/types";

import styles from "./adminTable.module.css";
import { hasPseudoFieldFilter, submittedMatchMode } from "./adminThreadFilters";

const columnHelper = createColumnHelper<AgentThreadRecord>();

/** Minimal agent identity the Agent cell needs to render a named link. */
interface AdminAgentInfo {
  agent_id: string;
  name: string;
}

/**
 * Mutable bag the column cell renderers dereference at render time. Stashed
 * in a ref so the column array stays referentially stable: AG Grid treats a
 * fresh column array as a structural reset (re-applying ``hide`` defaults and
 * rebuilding sort/widths), so per-render data — the selected row, feedback
 * counts, the agent id→info map, the click handlers — has to reach the cells
 * through a ref rather than by rebuilding the columns.
 */
interface CellContext {
  /** Settled agent lookups: agent, or null for an unresolvable id. An id
   *  still in flight is absent — the cell renders a skeleton for it. */
  agentById: Record<string, AdminAgentInfo | null>;
  selectedThreadId: string | undefined;
  feedbackCounts: Record<
    string,
    { positive: number; negative: number } | undefined
  >;
  onSelect: (threadId: string) => void;
  onOpenFeedback: (threadId: string) => void;
}

function FeedbackCell({
  counts,
  onOpen,
}: {
  counts: { positive: number; negative: number } | undefined;
  onOpen: () => void;
}): React.ReactNode {
  const theme = useTheme();
  if (!counts) {
    return <span>-</span>;
  }
  return (
    <span
      role="button"
      tabIndex={0}
      className={styles.feedbackCounts}
      onClick={(e) => {
        e.stopPropagation();
        onOpen();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          e.stopPropagation();
          onOpen();
        }
      }}
    >
      <ThumbUpAltIcon
        fontSize="inherit"
        sx={{ color: theme.palette.success.main }}
      />{" "}
      {counts.positive}{" "}
      <ThumbDownAltIcon
        fontSize="inherit"
        sx={{ color: theme.palette.error.main }}
      />{" "}
      {counts.negative}
    </span>
  );
}

/**
 * Build the admin threads column array. Reads live data off ``ctxRef`` at
 * render time (see {@link CellContext}); the array itself never changes
 * identity. ``Thread ID`` and ``Title`` are selection buttons (the viewer
 * renders in-page below the table rather than at a route, so there's no
 * ``Link`` to navigate to); ``Agent`` links out to the agent detail page.
 */
function buildColumns(
  ctxRef: React.MutableRefObject<CellContext>,
): ColDef<AgentThreadRecord>[] {
  const selectButton = (
    threadId: string,
    label: string,
    mono: boolean,
  ): React.ReactNode => {
    const selected = ctxRef.current.selectedThreadId === threadId;
    return (
      <button
        type="button"
        className={`${styles.linkButton} ${selected ? styles.linkButtonActive : ""} ${
          mono ? cellStyles.monoCell : ""
        }`}
        onClick={() => ctxRef.current.onSelect(threadId)}
      >
        {label}
      </button>
    );
  };

  return [
    columnHelper.accessor("thread_id", {
      headerName: "Thread ID",
      width: 200,
      cellRenderer: (p: ICellRendererParams<AgentThreadRecord, string>) =>
        p.data ? (
          <span className={styles.idCell}>
            {selectButton(p.data.thread_id, p.data.thread_id, true)}
            <CopyToClipboardButton
              text={p.data.thread_id}
              eventName="CopyToClipboardClicked"
              className={styles.copyButton}
            />
          </span>
        ) : null,
    }),
    columnHelper.accessor("title", {
      headerName: "Title",
      flex: 2,
      cellRenderer: (
        p: ICellRendererParams<AgentThreadRecord, string | undefined>,
      ) =>
        p.data
          ? selectButton(p.data.thread_id, p.data.title || "Untitled", false)
          : null,
    }),
    // Agent that launched the thread. Threads created outside an agent
    // template render an em-dash. The name comes from the per-page lookup in
    // `useAdminAgentsByIds`; while that lookup is still in flight — the
    // common case on page load — the cell shows a skeleton, and for an id
    // the endpoint can't resolve (e.g. a deleted agent) it falls back to the
    // raw id. An id beyond the hook's cap is never sent to the endpoint at
    // all, so it never leaves the "in flight" state: that cell stays a
    // skeleton forever. The `truncated` flag on `useAdminAgentsByIds` is
    // what surfaces that condition to the admin (see `agentLookupBanner`
    // below), since the cell itself has no way to distinguish "still
    // loading" from "will never resolve."
    columnHelper.accessor("created_from_agent_id", {
      headerName: "Agent",
      width: 180,
      cellRenderer: (
        p: ICellRendererParams<AgentThreadRecord, string | undefined | null>,
      ) => {
        const agentId = p.value;
        if (!agentId) {
          return <span>—</span>;
        }
        const agent = ctxRef.current.agentById[agentId];
        if (agent === undefined) {
          // Lookup still in flight: a skeleton, not the raw id — the id
          // flashing into a name on every load reads as a glitch.
          return <Skeleton width={120} data-testid="agent-cell-skeleton" />;
        }
        if (agent === null) {
          return (
            <span
              className={cellStyles.monoCell}
              data-testid="agent-cell-fallback"
            >
              {agentId}
            </span>
          );
        }
        return (
          <Link
            to={Goto.createAgentRoute(agent.agent_id)}
            className={styles.titleLink}
          >
            {agent.name}
          </Link>
        );
      },
    }),
    columnHelper.accessor("created_by", {
      headerName: "Created by",
      width: 220,
    }),
    columnHelper.accessor("org_id", {
      headerName: "Org",
      width: 200,
    }),
    columnHelper.timestamp("created", {
      headerName: "Created",
      width: 200,
      sortable: true,
      variant: "relative",
    }),
    columnHelper.accessor("model_profile", {
      headerName: "Model",
      width: 160,
      valueFormatter: (
        p: ValueFormatterParams<AgentThreadRecord, string | undefined>,
      ) => p.value || "-",
    }),
    // Not a row field — feedback counts are fetched separately and keyed by
    // thread id, so this column carries no accessor.
    {
      colId: "feedback",
      headerName: "Feedback",
      width: 120,
      sortable: false,
      cellRenderer: (p: ICellRendererParams<AgentThreadRecord>) => {
        const row = p.data;
        if (!row) {
          return null;
        }
        return (
          <FeedbackCell
            counts={ctxRef.current.feedbackCounts[row.thread_id]}
            onOpen={() => ctxRef.current.onOpenFeedback(row.thread_id)}
          />
        );
      },
    },
  ];
}

/** Single-value equality extraction for the legacy feedback-counts endpoint. */
function singleValue(filters: Filter[], field: string): string | undefined {
  const matches = filters.filter((f) => f.field === field);
  if (matches.length !== 1) {
    return undefined;
  }
  const filter = matches[0];
  if (filter.values.length !== 1) {
    return undefined;
  }
  if (filter.type === "string") {
    return filter.values[0];
  }
  if (filter.type === "enum") {
    return filter.values[0].value;
  }
  return undefined;
}

interface AgentThreadsTableProps {
  selectedAgentThreadId: string | undefined;
  onSelectAgentThread: (threadId: string) => void;
  onOpenFeedbackForAgentThread: (threadId: string) => void;
  /** Lazy server-search resolver for the Org filter dropdown. */
  orgOptions: FilterOptionsResolver;
  /** Lazy server-search resolver for the User filter dropdown. */
  userOptions: FilterOptionsResolver;
  /** Lazy server-search resolver for the Agent filter dropdown. */
  agentOptions: FilterOptionsResolver;
}

/**
 * Admin threads table on the ResourceView/DataTable stack.
 *
 * Owns its own filter state (``useResourceView``) and pagination, composing a
 * ``SearchQueryBody`` that ``useAdminAgentThreadSearch`` POSTs to
 * ``v1/admin/threads/search``. Filters map onto the backend's admin whitelist:
 * ``org_id`` / ``created_by`` / ``created_from_agent_id`` filter generic
 * columns; ``has_errors`` and ``is_customer`` (the promoted "customer only"
 * control) are boolean pseudo-fields the backend renders as bespoke
 * predicates. Only ``Created`` is sortable — the backend sort whitelist is
 * ``created`` alone.
 *
 * Pagination uses the backend's continuation tokens via a ``page → token``
 * map, reset whenever filters / sort / page size change. Selecting a row
 * lifts the thread id to the parent (the viewer renders below the table).
 */
export function AgentThreadsTable({
  selectedAgentThreadId,
  onSelectAgentThread,
  onOpenFeedbackForAgentThread,
  orgOptions,
  userOptions,
  agentOptions,
}: AgentThreadsTableProps) {
  // Live cell context, refreshed every render. Cells dereference this ref so
  // the column array can stay referentially stable (see CellContext). The
  // agent lookup starts empty and is filled from the per-page resolution below.
  const ctxRef = React.useRef<CellContext>({
    agentById: {},
    selectedThreadId: selectedAgentThreadId,
    feedbackCounts: {},
    onSelect: onSelectAgentThread,
    onOpenFeedback: onOpenFeedbackForAgentThread,
  });

  // Built once; stable for the table's lifetime.
  const columns = React.useMemo(() => buildColumns(ctxRef), []);

  const columnOptions = React.useMemo<ColumnPickerOption[]>(
    () =>
      columns.map((c) => ({ id: colId(c), label: c.headerName ?? colId(c) })),
    [columns],
  );

  // Fields exposed in the filter widget. Must stay within the backend's
  // ``ADMIN_SEARCH_FILTERABLE_FIELDS`` whitelist. Org / User / Agent use
  // labeled enum options so the dropdown shows friendly names while the wire
  // value stays the opaque id the backend filters on. ``has_errors`` and
  // ``is_customer`` are boolean pseudo-fields — the backend requires EQUALS +
  // a boolean value (the widget's default), so leave the comparator on
  // EQUALS rather than EXISTS/NOT_EXISTS.
  const filterDefinitions = React.useMemo<FilterDefinition[]>(
    () => [
      { type: "enum", field: "org_id", name: "Org", options: orgOptions },
      { type: "enum", field: "created_by", name: "User", options: userOptions },
      { type: "string", field: "thread_id", name: "Thread ID" },
      {
        type: "enum",
        field: "created_from_agent_id",
        name: "Agent",
        options: agentOptions,
      },
      {
        type: "enum",
        field: "status",
        name: "Status",
        options: Object.values(AgentThreadStatus),
      },
      {
        type: "enum",
        field: "visibility",
        name: "Visibility",
        options: Object.values(ThreadVisibility),
      },
      { type: "date", field: "created", name: "Created" },
      // These boolean pseudo-fields are EQUALS-only by default (not
      // ``nullable``), which is exactly what the structured admin search needs:
      // it 400s on the valueless presence form and accepts only ``EQUALS`` + a
      // boolean value.
      { type: "boolean", field: "has_errors", name: "Has errors" },
      { type: "boolean", field: "is_customer", name: "Customer only" },
    ],
    [orgOptions, userOptions, agentOptions],
  );

  const view = useResourceView<AgentThreadRecord>({
    getRowId: (row) => row.thread_id,
    initialSort: { columnId: "created", direction: "desc" },
    defaultVisibleColumnIds: columns.map(colId),
  });

  const userFilters: Filter[] =
    view.filters.type === "structured" ? view.filters.filters : [];
  const userMatchMode: FilterMatchMode =
    view.filters.type === "structured" ? view.filters.matchMode : "AND";

  // ``has_errors`` / ``is_customer`` are boolean pseudo-fields the backend
  // only accepts at the top level of an AND group: an OR match mode would wrap
  // them in an OR group and the search 400s. Force AND for the submitted
  // condition whenever one is present, and snap the match-mode toggle back to
  // AND below so the picker never claims an OR the backend won't honor.
  const pseudoFieldActive = hasPseudoFieldFilter(userFilters);
  const condition = composeFilters(
    [],
    userFilters,
    submittedMatchMode(userFilters, userMatchMode),
  );

  const setMatchMode = view.setMatchMode;
  React.useEffect(() => {
    if (pseudoFieldActive && userMatchMode === "OR") {
      setMatchMode("AND");
    }
  }, [pseudoFieldActive, userMatchMode, setMatchMode]);

  // Feedback counts are resolved server-side from the same simple equality
  // filters the legacy endpoint understands; richer filters (agent, status,
  // customer-only) just leave the param undefined — extra count keys are
  // harmless because the column reads by thread id.
  const feedbackCounts = useAdminFeedbackCountsForAgentThreads({
    orgId: singleValue(userFilters, "org_id"),
    createdBy: singleValue(userFilters, "created_by"),
    sessionId: singleValue(userFilters, "thread_id"),
  });

  // Token chain for forward pagination: the entry at key ``N`` is the
  // ``after`` token to send when fetching page N. Reset when the search shape
  // changes (``useResourceView`` already resets ``currentPage`` to 0 then).
  const [tokenChain, setTokenChain] = React.useState<Map<number, string>>(
    new Map(),
  );
  React.useEffect(() => {
    setTokenChain(new Map());
  }, [view.filters, view.table.sort, view.table.pageSize]);

  const after =
    view.table.currentPage > 0
      ? tokenChain.get(view.table.currentPage)
      : undefined;
  const query: SearchQueryBody = {
    ...(view.table.sort && {
      sort_by: view.table.sort.columnId,
      sort_direction: view.table.sort.direction.toUpperCase() as SortDirection,
    }),
    ...(condition && { condition }),
    ...(after && { after }),
    limit: view.table.pageSize,
  };

  const activeQuery = useAdminAgentThreadSearch({ query });

  // Resolve agent names for just the *current page's* rows. Collect the
  // page's ``created_from_agent_id`` values — duplicates and order don't
  // matter, ``useAdminAgentsByIds`` normalizes (dedupes + sorts) internally
  // — and hand them to the hook, which looks up whatever isn't already
  // cached cross-org in a single batched call. Bounded by page size — no
  // all-orgs directory walk. Unknown or since-deleted agents drop out of
  // ``byId`` and fall back to the raw id in the cell (see buildColumns).
  const pageAgentIds = React.useMemo(() => {
    const ids: string[] = [];
    for (const row of activeQuery.data?.items ?? []) {
      if (row.created_from_agent_id) {
        ids.push(row.created_from_agent_id);
      }
    }
    return ids;
  }, [activeQuery.data?.items]);

  const agentsByIdQuery = useAdminAgentsByIds(pageAgentIds);

  const agentById = React.useMemo(() => {
    const byId: Record<string, AdminAgentInfo | null> = {};
    for (const [id, agent] of Object.entries(agentsByIdQuery.resolvedById)) {
      byId[id] = agent ? { agent_id: agent.agent_id, name: agent.name } : null;
    }
    return byId;
  }, [agentsByIdQuery.resolvedById]);

  const nextToken = activeQuery.data?.next_token ?? null;
  const currentPage = view.table.currentPage;
  React.useEffect(() => {
    if (nextToken === null) {
      return;
    }
    setTokenChain((prev) => {
      if (prev.get(currentPage + 1) === nextToken) {
        return prev;
      }
      const next = new Map(prev);
      next.set(currentPage + 1, nextToken);
      return next;
    });
  }, [nextToken, currentPage]);

  // Keep the cell-context ref current, then repaint the cells that read it.
  ctxRef.current = {
    agentById,
    selectedThreadId: selectedAgentThreadId,
    feedbackCounts: feedbackCounts.data ?? {},
    onSelect: onSelectAgentThread,
    onOpenFeedback: onOpenFeedbackForAgentThread,
  };
  const refreshCells = view.table.refreshCells;
  React.useEffect(() => {
    refreshCells();
  }, [refreshCells, selectedAgentThreadId, feedbackCounts.data, agentById]);

  const loading = deriveLoadingState({
    isLoading: activeQuery.isLoading,
    isFetching: activeQuery.isFetching,
  });

  const rows = activeQuery.data?.items ?? [];

  // Either failure mode leaves some cells permanently unresolved (a raw id,
  // or — for a truncated id beyond the hook's cap — a skeleton that will
  // never settle), so both surface the same admin-visible warning.
  const agentLookupBanner =
    agentsByIdQuery.isError || agentsByIdQuery.truncated ? (
      <Alert severity="warning" className={styles.banner}>
        {agentsByIdQuery.isError
          ? "Failed to resolve some agent names; their raw ids are shown instead."
          : "Some agent names could not be resolved: too many distinct agents on this page."}
      </Alert>
    ) : null;

  if (activeQuery.isError) {
    return (
      <Alert severity="error" className={styles.banner}>
        Failed to load threads. {activeQuery.error?.message ?? ""}
      </Alert>
    );
  }

  return (
    <div className={styles.tableSlot}>
      {agentLookupBanner}
      <ResourceView.Root>
        <ResourceView.Actions>
          <ResourceView.Filters
            filterDefinitions={filterDefinitions}
            onFilterSelected={view.commit}
          />
          <ColumnVisibility
            options={columnOptions}
            visibleColumnIds={view.table.visibleColumnIds}
            setColumnVisibility={view.table.setColumnVisibility}
          />
        </ResourceView.Actions>
        <ResourceView.ActiveFilters
          filters={view.filters}
          filterDefinitions={filterDefinitions}
          onRemove={view.removeFilter}
          onUpdate={view.updateFilter}
          onClear={view.clearFilters}
          onMatchModeChange={view.setMatchMode}
          onFilterSelected={view.commit}
        />
        <DataTable<AgentThreadRecord>
          table={view.table}
          hasNextPage={Boolean(activeQuery.data?.next_token)}
          loading={loading}
          columns={columns}
          data={rows}
          noRowsContent="No threads found."
        />
      </ResourceView.Root>
    </div>
  );
}
